import { useId, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { Panel } from './Panel';
import { Field } from './Field';
import { ImageField } from './ImageField';
import { inputCls } from './styles';
import { SEO_DESCRIPTION_MAX, SEO_TITLE_MAX, cleanSeo, effectiveSeo, type SeoResolved } from '../../core/rules/seo';
import { SITE_URL } from '../../data/seo';
import type { SeoFields } from '../../core/types/seo';
type Props = {
  seo: SeoFields | undefined;
  onChange: (seo: SeoFields | undefined) => void;
  /** Wat de site zelf maakt als het veld leeg is; staat grijs in het veld. */
  auto: SeoResolved;
  /** Het adres zoals Google het toont, bijv. "/webshop/serum". `null` = nog geen eigen pagina. */
  path: string | null;
  /** Wat de deelafbeelding is als het veld leeg is, bijv. "de productfoto". */
  imageFallback: string;
};
const host = SITE_URL.replace(/^https?:\/\//, '');

/**
 * "Vindbaarheid in Google": eigen titel, beschrijving en deelbeeld, met een voorbeeld zoals
 * Google en WhatsApp het tonen. Leeg = automatisch; de automatische tekst staat grijs in het veld.
 * Ingeklapt tot ze hem opent: de meeste producten hebben genoeg aan de automatische tekst.
 */
export function SeoPanel({
  seo,
  onChange,
  auto,
  path,
  imageFallback
}: Props) {
  const own = Boolean(seo?.title || seo?.description || seo?.image);
  const [open, setOpen] = useState(own);
  const id = useId();
  const shown = effectiveSeo(seo, auto);
  const set = (patch: SeoFields) => onChange(cleanSeo({
    ...seo,
    ...patch
  }));
  return <Panel title="Vindbaarheid in Google" description={own ? 'Met eigen tekst.' : 'Automatisch uit de naam en de korte tekst.'} actions={<button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls={id} className="inline-flex min-h-[40px] items-center gap-1 rounded-md px-2 text-[13px] text-ink hover:bg-sand" data-id="element-5170" data-mp="mylw2p60wv06">
          {open ? 'Inklappen' : 'Aanpassen'}
          <ChevronDownIcon className={`h-4 w-4 transition-transform duration-150 ease-soft ${open ? 'rotate-180' : ''}`} strokeWidth={1.75} aria-hidden="true" data-id="element-5171" data-mp="1ucppm28xtup3" />
        </button>} data-id="element-5169" data-mp="dyyzcgo8oddg">
      <GooglePreview title={shown.title} description={shown.description} path={path} data-id="element-5172" data-mp="2429a1ccatwm2" />

      {path === null && <p className="mt-3 text-[12px] leading-relaxed text-muted" data-id="element-5173" data-mp="291c3s0mtwgeq">
          Dit heeft nog geen eigen pagina: de tekst wordt gebruikt zodra die er is, en bij delen.
        </p>}

      {open && <div id={id} className="mt-5 space-y-5 border-t border-line pt-5" data-id="element-5174" data-mp="1gdg88z8zwujj">
          <Field label="Titel in Google" count={{
        value: shown.title.length,
        max: SEO_TITLE_MAX
      }} hint="Leeg = automatisch. Het belangrijkste woord vooraan." data-id="element-5175" data-mp="1lbrgx6nk0z06">
            <input value={seo?.title ?? ''} onChange={e => set({
          title: e.target.value
        })} placeholder={auto.title} className={inputCls} data-id="element-5176" data-mp="1qbvxsxsdjvqx" />
          </Field>
          <Field label="Beschrijving in Google" count={{
        value: shown.description.length,
        max: SEO_DESCRIPTION_MAX
      }} hint="Leeg = automatisch. Eén of twee zinnen die zeggen waarom iemand moet klikken." data-id="element-5177" data-mp="31oydzrqkajak">
            <textarea value={seo?.description ?? ''} onChange={e => set({
          description: e.target.value
        })} placeholder={auto.description} rows={3} className={`${inputCls} resize-y leading-relaxed`} data-id="element-5178" data-mp="l5npso9m8f1c" />
          </Field>
          <Field label="Afbeelding bij delen" as="div" hint={`Leeg = ${imageFallback}. Liggend werkt het best (1200 × 630).`} data-id="element-5179" data-mp="6c2eps0qy16e">
            <ImageField value={seo?.image ?? ''} onChange={image => set({
          image
        })} fit="cover" aspect="aspect-[1200/630]" data-id="element-5180" data-mp="3til8des3n1ty" />
          </Field>
          <SharePreview title={shown.title} description={shown.description} image={shown.image} data-id="element-5181" data-mp="3oll33632q33w" />
        </div>}
    </Panel>;
}
function GooglePreview({
  title,
  description,
  path
}: {
  title: string;
  description: string;
  path: string | null;
}) {
  return <figure className="rounded-md bg-shell px-4 py-3" data-id="element-5182" data-mp="kezcbcr8xl75">
      <figcaption className="sr-only" data-id="element-5183" data-mp="kd4nvogrnayb">Zo staat het in Google</figcaption>
      <p className="truncate text-[12px] text-muted" data-id="element-5184" data-mp="ogaskro8cu2o">
        {host}
        {path && path !== '/' ? ` › ${path.replace(/^\//, '').split('/').join(' › ')}` : ''}
      </p>
      <p className="mt-0.5 line-clamp-1 text-[17px] leading-snug text-[#1a0dab]" data-id="element-5185" data-mp="ffcxiyvrrww8">{cut(title, SEO_TITLE_MAX)}</p>
      <p className="mt-0.5 line-clamp-2 text-[13px] leading-relaxed text-ink/80" data-id="element-5186" data-mp="q709gghsgqz">{description ? cut(description, SEO_DESCRIPTION_MAX) : 'Google kiest zelf een stukje tekst van de pagina.'}</p>
    </figure>;
}
function SharePreview({
  title,
  description,
  image
}: {
  title: string;
  description: string;
  image: string;
}) {
  return <figure data-id="element-5187" data-mp="3ssy6q9ykkyij">
      <figcaption className="mb-1.5 text-[13px] font-medium text-ink" data-id="element-5188" data-mp="3ssoe0em54d95">Zo ziet het eruit in WhatsApp of op Facebook</figcaption>
      <div className="max-w-sm overflow-hidden rounded-md border border-line bg-shell" data-id="element-5189" data-mp="3ixed5u7habwy">
        {image ? <img src={image} alt="" className="aspect-[1200/630] w-full object-cover" loading="lazy" data-id="element-5190" data-mp="2zaji413u5s37" /> : <div className="aspect-[1200/630] w-full bg-sand" data-id="element-5191" data-mp="2v9r9na2sfr7z" />}
        <div className="px-3 py-2" data-id="element-5192" data-mp="2vbat19jqqidz">
          <p className="text-[11px] uppercase text-muted" data-id="element-5193" data-mp="398zdig9wbnii">{host}</p>
          <p className="line-clamp-1 text-[14px] font-medium text-ink" data-id="element-5194" data-mp="39763qs3zkn6a">{title}</p>
          <p className="line-clamp-1 text-[12px] text-muted" data-id="element-5195" data-mp="348l4auwk67iw">{description}</p>
        </div>
      </div>
    </figure>;
}

/** Zoals Google afkapt: op ± de grens, met "…". */
const cut = (s: string, max: number) => s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;